/**
 * LLM config slice (PX-W6.5) — the per-project agent-type rows.
 *
 * INV-2 enforcement point: the save thunk runs
 * {@link resolveLlmKeySubmission} BEFORE any HTTP. A refused form never
 * reaches the wire, so the store's verbatim-key semantics cannot be tripped
 * by an empty or masked field. NO reducer touches key material — the
 * fulfilled payload is the masked row.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import { resolveLlmKeySubmission, type LlmKeyForm, type LlmKeyRefusal } from './llmKeyPolicy'
import * as service from './services/llmConfigService.http'
import type { LlmConfig } from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

interface ProjectKeyed<T> {
  key: string
  value: T
}

/** The rejection payload for a client-side refusal (no HTTP happened). */
export interface LlmRefusal {
  reason: LlmKeyRefusal
}

export const fetchLlmConfigs = createAsyncThunk<
  ProjectKeyed<LlmConfig[]>,
  string,
  { rejectValue: ApiError }
>(
  'llm/fetchLlmConfigs',
  async (projectId, { getState, rejectWithValue }) => {
    try {
      return {
        key: `${orgKey(getState) ?? 'none'}:${projectId}`,
        value: await service.llmConfigService.list(projectId)
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (projectId, { getState }) =>
      shouldFetch(
        (getState() as { llm: LlmConfigState }).llm.cache,
        `${orgKey(getState) ?? 'none'}:${projectId}`
      )
  }
)

/**
 * Save one row. The pure key decision runs first: `ok:false` rejects with the
 * REASON (the page maps it to i18n) and nothing is sent. The form's `newKey`
 * never appears in Redux state — only the decision's payload does, and only
 * inside the thunk's closure.
 */
export const saveLlmConfig = createAsyncThunk<
  LlmConfig,
  { projectId: string; form: LlmKeyForm },
  { rejectValue: ApiError | LlmRefusal }
>('llm/saveLlmConfig', async ({ projectId, form }, { rejectWithValue }) => {
  const decision = resolveLlmKeySubmission(form)
  if (!decision.ok) {
    return rejectWithValue({ reason: decision.reason })
  }
  try {
    return await service.llmConfigService.save(projectId, decision.payload)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export interface LlmConfigState {
  configs: LlmConfig[]
  cache: FetchCache
  saving: boolean
  error: ApiError | null
  /** The last client-side refusal reason (cleared on the next attempt). */
  refusal: LlmKeyRefusal | null
  mutationError: ApiError | null
}

const initialState: LlmConfigState = {
  configs: [],
  cache: initialFetchCache,
  saving: false,
  error: null,
  refusal: null,
  mutationError: null
}

export const llmConfigSlice = createSlice({
  name: 'llm',
  initialState,
  reducers: {
    llmErrorDismissed(state) {
      state.error = null
      state.mutationError = null
      state.refusal = null
    },
    llmRetryClicked(state) {
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLlmConfigs.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchLlmConfigs.fulfilled, (state, action: PayloadAction<ProjectKeyed<LlmConfig[]>>) => {
        fetchSucceeded(state.cache, action.payload.key)
        state.configs = action.payload.value
      })
      .addCase(fetchLlmConfigs.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(saveLlmConfig.pending, (state) => {
        state.saving = true
        state.refusal = null
        state.mutationError = null
      })
      .addCase(saveLlmConfig.fulfilled, (state, action: PayloadAction<LlmConfig>) => {
        state.saving = false
        // Merge by agentType — one row per (project, agentType) server-side.
        const at = state.configs.findIndex((row) => row.agentType === action.payload.agentType)
        if (at >= 0) state.configs[at] = action.payload
        else state.configs.push(action.payload)
        // The next read refetches the server's masked truth.
        invalidate(state.cache)
      })
      .addCase(saveLlmConfig.rejected, (state, action) => {
        state.saving = false
        const payload = action.payload
        if (payload !== undefined && 'reason' in payload) {
          state.refusal = payload.reason
        } else {
          state.mutationError = (payload as ApiError | undefined) ?? null
        }
      })
  }
})

export const { llmErrorDismissed, llmRetryClicked } = llmConfigSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithLlm {
  llm: LlmConfigState
}

export const selectLlmConfigs = (s: WithLlm): LlmConfig[] => s.llm.configs
export const selectLlmCache = (s: WithLlm): FetchCache => s.llm.cache
export const selectLlmError = (s: WithLlm): ApiError | null => s.llm.error
export const selectLlmSaving = (s: WithLlm): boolean => s.llm.saving
export const selectLlmRefusal = (s: WithLlm): LlmKeyRefusal | null => s.llm.refusal
export const selectLlmMutationError = (s: WithLlm): ApiError | null => s.llm.mutationError
